/**
 * Shared chrome for the web onboarding "basics" steps (this-is-you /
 * how-to-reach-you / who-sees-cv): the exact scaffold the polished CV form
 * steps use (see `StepExperience.web.tsx`): scroll region → 560 column → bare
 * back chevron → 28/600 two-tone title (accent `#5a2aff`) → 15px subtitle →
 * step content. Web-only (plain DOM, like `web-form-elements.tsx`); never
 * import from native code.
 */
import type { CSSProperties, ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../hooks/useLocale';
import { useOnboarding } from './OnboardingProvider';

interface OnboardingWebStepProps {
  titleBase: string;
  titleAccent: string;
  subtitle?: string;
  onBack: () => void;
  /** Optional-step affordance: renders a plain "Skip" text button opposite the back chevron. */
  onSkip?: () => void;
  children: ReactNode;
}

export function OnboardingWebStep({
  titleBase,
  titleAccent,
  subtitle,
  onBack,
  onSkip,
  children,
}: OnboardingWebStepProps) {
  const { t } = useTranslation('auth');
  const { isRTL } = useLocale();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <div style={{ maxWidth: 560, marginInline: 'auto', width: '100%' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}
          >
            <button
              type="button"
              onClick={onBack}
              aria-label={t('onboarding.back', { defaultValue: 'Back' })}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
            >
              <ChevronLeft
                size={24}
                color="var(--text-primary)"
                style={isRTL ? { transform: 'scaleX(-1)' } : undefined}
              />
            </button>
            {onSkip ? <OnboardingSkipButton onSkip={onSkip} /> : null}
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.01em', marginBottom: 4 }}>
            <span style={{ color: 'var(--text-secondary)' }}>{titleBase} </span>
            <span style={{ color: '#5a2aff' }}>{titleAccent}</span>
          </h1>
          {subtitle ? (
            <p style={{ fontSize: 15, color: 'var(--text-secondary)', marginBottom: 16 }}>{subtitle}</p>
          ) : null}
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Plain "Skip" text action for optional onboarding steps: sits opposite the
 * back chevron in the top row (mirrors the native scaffold's onSkip slot).
 */
export function OnboardingSkipButton({ onSkip }: { onSkip: () => void }) {
  const { t } = useTranslation('auth');
  return (
    <button
      type="button"
      onClick={onSkip}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 4,
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 500,
        color: 'var(--text-secondary)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.textDecoration = 'underline';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.textDecoration = 'none';
      }}
    >
      {t('onboarding.skip', { defaultValue: 'Skip' })}
    </button>
  );
}

interface OnboardingFooterNoteProps {
  text: string;
  linkLabel: string;
  onLinkClick: () => void;
}

/**
 * The small reassurance line above the step CTA ("You choose who sees what.
 * Privacy"). Neutral idiom: tertiary text with an underlined text button, no
 * accent color (matches the design skill's accent restraint).
 */
export function OnboardingFooterNote({ text, linkLabel, onLinkClick }: OnboardingFooterNoteProps) {
  return (
    <p style={footerNoteStyle}>
      {text}{' '}
      <button type="button" onClick={onLinkClick} style={footerLinkStyle}>
        {linkLabel}
      </button>
    </p>
  );
}

/**
 * Slim status line shown on the CV form steps while the background tier+style
 * enrichment (kicked off after AI input) is still writing: the sections on
 * these pages hydrate automatically when it lands.
 */
export function CvEnrichmentNotice() {
  const { t } = useTranslation('auth');
  const { cvEnrichment } = useOnboarding();

  if (cvEnrichment.status !== 'running') return null;

  return (
    <div style={noticeStyle} role="status">
      <span style={noticeSpinnerStyle} aria-hidden />
      <span>
        {t('onboarding.enrichment.working', {
          defaultValue:
            'Your AI is polishing this CV: updates will appear here automatically',
        })}
      </span>
    </div>
  );
}

const noticeStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 16px',
  borderRadius: 12,
  backgroundColor: 'rgba(0,0,0,0.04)',
  color: 'var(--text-secondary)',
  fontSize: 13,
  lineHeight: '18px',
  marginBottom: 16,
};

const noticeSpinnerStyle: CSSProperties = {
  width: 14,
  height: 14,
  flexShrink: 0,
  border: '2px solid rgba(94,41,255,0.25)',
  borderTopColor: '#5e29ff',
  borderRadius: '50%',
  animation: 'spin 0.8s linear infinite',
  display: 'inline-block',
};

const footerNoteStyle: CSSProperties = {
  margin: '20px 0 0',
  color: 'var(--text-tertiary)',
  fontSize: 13,
  lineHeight: '18px',
  textAlign: 'center',
};

const footerLinkStyle: CSSProperties = {
  padding: 0,
  color: 'var(--text-secondary)',
  fontWeight: 500,
  border: 0,
  background: 'none',
  font: 'inherit',
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  cursor: 'pointer',
};
